import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import ChartRenderer from './ChartRenderer';
import type { ChartSpec } from '../types/chat.types';

interface SummaryStatValue {
  count?: number;
  mean?: number;
  std?: number;
  min?: number;
  max?: number;
  [key: string]: unknown;
}

interface FlightDataPanelProps {
  flightData: {
    flight_id?: string;
    drone_serial?: string;
    flight_date?: string;
    row_count?: number;
    column_count?: number;
    columns?: string[];
    summary_stats?: Record<string, SummaryStatValue>;
    chart_data?: Record<string, unknown>[];
  };
  chartSpecs?: ChartSpec[];
  compact?: boolean;
}

const FlightDataPanel: React.FC<FlightDataPanelProps> = ({
  flightData,
  chartSpecs,
  compact = false,
}) => {
  const stats = flightData.summary_stats || {};
  const statEntries = Object.entries(stats).slice(0, compact ? 5 : 10);

  const relevantCharts = chartSpecs?.filter((c) => c.title.toLowerCase().includes('flight')) || [];
  const maxCharts = compact ? 1 : relevantCharts.length;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Flight metadata */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Flight Data — {flightData.drone_serial || 'Unknown'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {flightData.flight_date && (
          <Chip label={`Date: ${flightData.flight_date}`} size="small" variant="outlined" />
        )}
        {flightData.row_count != null && (
          <Chip
            label={`${flightData.row_count.toLocaleString()} samples`}
            size="small"
            variant="outlined"
          />
        )}
        {flightData.column_count != null && (
          <Chip label={`${flightData.column_count} columns`} size="small" variant="outlined" />
        )}
      </Box>

      {/* Summary stats table */}
      {statEntries.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mb: 1.5, maxHeight: compact ? 150 : 300 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Parameter</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  Mean
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  Min
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  Max
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  Std
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statEntries.map(([param, s]) => (
                <TableRow key={param} hover>
                  <TableCell sx={{ fontSize: '0.7rem' }}>{param.replace(/_/g, ' ')}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                    {s.mean != null ? s.mean.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                    {s.min != null ? s.min.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                    {s.max != null ? s.max.toFixed(2) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                    {s.std != null ? s.std.toFixed(2) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Time-series charts */}
      {relevantCharts.slice(0, maxCharts).map((spec, i) => (
        <ChartRenderer key={i} spec={spec} height={compact ? 180 : 250} />
      ))}
    </Box>
  );
};

export default FlightDataPanel;
